import React from 'react';
import { TypeMaster } from '../../../../../../services/apiTypes';
import { MANAGE_ITEM_MODE } from '../../../../../../_constants/manageConst';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { MANAGE_ITEM_ROW_TYPE } from '../../../../../../_constants/manageConst';
import {
  CategoryMaster,
  ItemMaster,
} from '../../../../../../services/apiTypes';
import useSortItem from '../../../../hooks/useSortItem';
import EditDepth0Row from './EditDepth0Row';
import { DisplayDepth0Row } from './DisplayDepth0Row';
import { Options } from '../../../../ManageInspectionItem';
import ItemDepth1Items from './Depth1Items/Depth1Items';
import { theme } from '../../../../../../theme';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material';

type itemDepth0ItemsProps = {
  type: TypeMaster;
  targetItem: ItemMaster;
  categoryMaster: CategoryMaster[];
  setCategoryMaster: React.Dispatch<React.SetStateAction<CategoryMaster[]>>;
  typeMaster: TypeMaster[];
  setTypeMaster: React.Dispatch<React.SetStateAction<TypeMaster[]>>;
  itemMaster: ItemMaster[];
  setItemMaster: React.Dispatch<React.SetStateAction<ItemMaster[]>>;
  displayMode: number;
  isAdmin: boolean;
  isOpenAccordions: Record<string, boolean>;
  handleAccordion: (id: string) => void;
  stickyPosition: number[];
  isSetPFRCodes: (item: ItemMaster) => boolean;
  isDisabledBtn: boolean;
  options: Options;
  onClickItemAddIcon: (data: ItemMaster) => void;
  onClickItemEditIcon: (data: ItemMaster) => void;
  handleItemInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    item: ItemMaster,
  ) => ItemMaster;
  checkInputForCondition: (item: ItemMaster) => boolean;
  onClickItemEditSave: (item: ItemMaster) => void;
  onClickItemEditCancel: () => void;
  onClickItemNamesEdit: (item: ItemMaster) => void;
  onClickItemDeleteIcon: (data: ItemMaster) => void;
};

const ItemDepth0Items: React.FC<itemDepth0ItemsProps> = ({
  type,
  targetItem,
  categoryMaster,
  setCategoryMaster,
  typeMaster,
  setTypeMaster,
  itemMaster,
  setItemMaster,
  displayMode,
  isAdmin,
  isOpenAccordions,
  handleAccordion,
  stickyPosition,
  isSetPFRCodes,
  isDisabledBtn,
  options,
  onClickItemAddIcon,
  onClickItemEditIcon,
  handleItemInputChange,
  checkInputForCondition,
  onClickItemNamesEdit,
  onClickItemEditSave,
  onClickItemEditCancel,
  onClickItemDeleteIcon,
}) => {
  const { SortableItem } = useSortItem({
    categoryMaster,
    setCategoryMaster,
    typeMaster,
    setTypeMaster,
    itemMaster,
    setItemMaster,
    displayMode,
    isSetPFRCodes,
    targetItem,
  });

  const multiAutoCompleteMenu = (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: React.Key;
    },
    selected: boolean,
    itemName: string,
  ) => {
    const { key, ...optionProps } = props;
    const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon;
    return (
      <li key={key} {...optionProps}>
        <SelectionIcon
          fontSize="small"
          style={{
            marginRight: 8,
            padding: 2,
            boxSizing: 'content-box',
            color: theme.palette.primary.main,
          }}
        />
        {itemName}
      </li>
    );
  };

  return (
    <SortableContext
      id={MANAGE_ITEM_ROW_TYPE.ITEM}
      items={itemMaster
        .filter((i) => {
          return i.typeId === type.typeId && i.depth === 0;
        })
        .map((item) => item.itemId)}
      strategy={verticalListSortingStrategy}
    >
      {itemMaster
        .filter((i) => {
          return i.typeId === type.typeId && i.depth === 0;
        })
        .map((item0) => {
          // 子項目数を取得する
          const items0 = itemMaster.filter((i) => {
            if (i.typeId !== type.typeId || i.depth === 0) {
              return false;
            }
            // depth0以外のitem
            if (i.depth === 1) {
              return i.parentItemId === item0.itemId;
            } else {
              // 階層2は階層1を取得
              const depth1 =
                itemMaster.find((i2) => i2.itemId === i.parentItemId) ?? '';
              if (depth1) {
                // この項目が親で開いている
                return (
                  depth1.parentItemId === item0.itemId &&
                  isOpenAccordions[depth1.itemId]
                );
              }
              return false;
            }
          }).length;

          // 編集または追加モードで対象itemIdと一致したらeditDepth表示にする
          const isEditItem =
            (displayMode === MANAGE_ITEM_MODE.ADD ||
              displayMode === MANAGE_ITEM_MODE.EDIT) &&
            targetItem.itemId === item0.itemId;

          return (
            // 中項目が開いていれば表示する
            isOpenAccordions[item0.typeId] && (
              <React.Fragment
                key={`${MANAGE_ITEM_ROW_TYPE.ITEM}_${item0.itemId}`}
              >
                <SortableItem
                  key={`item_${item0.itemId}`}
                  id={item0.itemId}
                  name={MANAGE_ITEM_ROW_TYPE.ITEM}
                >
                  {isEditItem ? (
                    <EditDepth0Row
                      item={targetItem}
                      options={options}
                      mode={displayMode}
                      isAdmin={isAdmin}
                      stickyPosition={stickyPosition}
                      countItems={items0}
                      isOpenAccordion={isOpenAccordions[item0.itemId]}
                      isDisabledBtn={isDisabledBtn}
                      displayType={type.displayType}
                      handleInputChange={handleItemInputChange}
                      checkInputForCondition={checkInputForCondition}
                      onClickOk={onClickItemEditSave}
                      onClickCancel={onClickItemEditCancel}
                      onClickNamesEdit={onClickItemNamesEdit}
                      multiAutoCompleteMenu={multiAutoCompleteMenu}
                    />
                  ) : (
                    <DisplayDepth0Row
                      item={item0}
                      mode={displayMode}
                      isAdmin={isAdmin}
                      stickyPosition={stickyPosition}
                      countItems={items0}
                      isOpenAccordion={isOpenAccordions[item0.itemId]}
                      displayType={type.displayType}
                      onClickAccordion={handleAccordion}
                      onClickAddIcon={onClickItemAddIcon}
                      onClickEditIcon={onClickItemEditIcon}
                      onClickDeleteIcon={onClickItemDeleteIcon}
                    />
                  )}
                </SortableItem>
                <ItemDepth1Items
                  type={type}
                  item0={item0}
                  targetItem={targetItem}
                  categoryMaster={categoryMaster}
                  setCategoryMaster={setCategoryMaster}
                  typeMaster={typeMaster}
                  setTypeMaster={setTypeMaster}
                  itemMaster={itemMaster}
                  setItemMaster={setItemMaster}
                  displayMode={displayMode}
                  isOpenAccordions={isOpenAccordions}
                  isAdmin={isAdmin}
                  stickyPosition={stickyPosition}
                  isDisabledBtn={isDisabledBtn}
                  handleAccordion={handleAccordion}
                  options={options}
                  handleItemInputChange={handleItemInputChange}
                  checkInputForCondition={checkInputForCondition}
                  onClickItemEditIcon={onClickItemEditIcon}
                  onClickItemNamesEdit={onClickItemNamesEdit}
                  onClickItemAddIcon={onClickItemAddIcon}
                  onClickItemEditCancel={onClickItemEditCancel}
                  onClickItemEditSave={onClickItemEditSave}
                  onClickItemDeleteIcon={onClickItemDeleteIcon}
                  isSetPFRCodes={isSetPFRCodes}
                  multiAutoCompleteMenu={multiAutoCompleteMenu}
                />
              </React.Fragment>
            )
          );
        })}
    </SortableContext>
  );
};

export default ItemDepth0Items;
